import { Action, Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators';
import axios from 'axios';
import store from '@/store';
import { Content } from '@/types/content';

export interface ContentsStatus {
  interval: number;
  wait: number;
  limit: number;
  current: Content | null;
  url: string;
  isVideo: boolean;
  isPlaying: boolean;
}

@Module({ dynamic: true, store, name: 'contents', namespaced: true, preserveState: false })
class ContentsModlue extends VuexModule implements ContentsStatus {
  /**
   * インターバル間隔
   *
   * @type {number}
   * @memberof ContentsModlue
   */
  public interval = 10 * 1000;

  /**
   * 自動再生復帰までの待機時間
   *
   * @type {number}
   * @memberof ContentsModlue
   */
  public wait = 0;

  /**
   * 表示制限数
   *
   * @type {number}
   * @memberof ContentsModlue
   */
  public limit = 0;

  /**
   * カタログ情報
   *
   * @type {Content[]}
   * @memberof ContentsModlue
   */
  private datas: Content[] = [];

  /**
   * 現在表示中のカタログインデックス
   *
   * @type {number}
   * @memberof ContentsModlue
   */
  private _index = 0;

  /**
   * 再生中かどうか
   *
   * @type {boolean}
   * @memberof ContentsModlue
   */
  private _playing = false;

  /**
   * ビデオエレメント
   *
   * @type {HTMLVideoElement | null}
   * @memberof ContentsModlue
   */
  private _video: HTMLVideoElement | null = null;

  /**
   * タイマーID
   *
   * @type {NodeJS.Timeout}
   * @memberof ContentsModlue
   */
  private _timerId?: NodeJS.Timeout;

  /**
   * 現在表示中の広告データを取得
   *
   * @readonly
   * @type {(Content | null)}
   * @memberof ContentsModlue
   */
  public get current(): Content | null {
    if (this.datas.length > 0) {
      return this.datas[this._index];
    }
    return null;
  }

  /**
   * 現在表示中のメディアのURLを取得
   *
   * @readonly
   * @type {string}
   * @memberof ContentsModlue
   */
  public get url(): string {
    let url = '';
    const content = this.current;
    if (content !== null) {
      url = content.url;
    }
    return url;
  }

  /**
   * 現在表示中の MIME Type が動画かどうか
   *
   * @readonly
   * @type {boolean}
   * @memberof ContentsModlue
   */
  public get isVideo(): boolean {
    const content = this.current;
    const mimetypeList = ['video/mp4'];
    if (content !== null && mimetypeList.indexOf(content.mimetype) !== -1) {
      return true;
    }
    return false;
  }

  /**
   * 再生中かどうかを取得
   *
   * @readonly
   * @type {boolean}
   * @memberof ContentsModlue
   */
  public get isPlaying(): boolean {
    return this._playing;
  }

  /**
   * インターバル間隔をセット
   *
   * @param {number} value
   * @memberof ContentsModlue
   */
  @Mutation
  setInterval(value: number) {
    this.interval = value;
  }

  /**
   * 自動再生復帰までの待機時間をセット
   *
   * @param {number} value
   * @memberof ContentsModlue
   */
  @Mutation
  setWait(value: number) {
    this.wait = value;
  }

  /**
   * 表示制限数
   *
   * @param {number} value
   * @memberof ContentsModlue
   */
  @Mutation
  setLimit(value: number) {
    this.limit = value;
  }

  /**
   * カタログ情報をセット
   *
   * @param {Content[]} value
   * @memberof ContentsModlue
   */
  @Mutation
  setDatas(value: Content[]) {
    this.datas = value;
  }

  /**
   * 現在表示中のカタログインデックスをセット
   *
   * @param {number} value
   * @memberof ContentsModlue
   */
  @Mutation
  setIndex(value: number) {
    this._index = value;
  }

  /**
   * 再生中かどうかをセット
   *
   * @param {boolean} value
   * @memberof ContentsModlue
   */
  @Mutation
  setPlaying(value: boolean) {
    this._playing = value;
  }

  /**
   * VIDEOエレメントをセット
   *
   * @param {HTMLVideoElement} value
   * @memberof ContentsModlue
   */
  @Mutation
  SET_VIDEO(value: HTMLVideoElement) {
    this._video = value;
  }

  /**
   * 画像コンテンツ読み込み開始
   *
   * @returns {Promise<boolean>}
   * @memberof ContentsModlue
   */
  @Action({})
  async load(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const config = {
        params: {
          limit: this.limit
        }
      };
      axios
        .get(process.env.VUE_APP_API_DOMAIN + '/api/contents', config)
        .then(response => {
          const contents: Content[] = response.data;
          Promise.all(
            contents.map(async content => {
              if (content.mimetype === 'image/jpeg' || content.mimetype === 'image/png') {
                content.url = await this.loadImage(content.url);
              }
              return content;
            })
          ).then((contents: Content[]) => {
            // 全ての画像読み込みが完了したら、データ適応し再生開始
            this.setDatas(contents);
            this.setIndex(0);
            resolve();
          });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  /**
   * 画像読み込み
   *
   * @param {string} url
   * @returns {Promise<string>}
   * @memberof ContentsModlue
   */
  @Action({})
  async loadImage(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      axios
        .get(url, { responseType: 'arraybuffer' })
        .then(response => {
          const prefix = `data:${response.headers['content-type']};base64,`;
          const base64 = new Buffer(response.data, 'binary').toString('base64');
          resolve(prefix + base64);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  /**
   * 読み込んだ画像コンテンツを削除
   *
   * @memberof ContentsModlue
   */
  @Action({})
  delete() {
    this._timerId && clearTimeout(this._timerId);
    if (this._video) {
      this._video.pause();
    }
  }

  /**
   * 前のコンテンツへ進む
   *
   * @memberof ContentsModlue
   */
  @Action({})
  prev() {
    let index = this._index - 1;
    if (index < 0) {
      index = this.datas.length - 1;
    }
    this.setIndex(index);
  }

  /**
   * 次のコンテンツへ進む
   *
   * @memberof ContentsModlue
   */
  @Action({})
  next() {
    this.delete();
    let index = this._index + 1;
    if (index > this.datas.length - 1) {
      index = 0;
    }
    this.setIndex(index);
  }

  /**
   * 自動再生開始
   *
   * @memberof ContentsModlue
   */
  @Action({})
  play() {
    this._timerId && clearTimeout(this._timerId);
    this.setPlaying(true);
    if (this.isVideo && this._video) {
      // 動画のときは、再生再生メソッドを呼び出す
      this._video.play();
    } else {
      this._timerId = setTimeout(() => {
        this.next();
      }, this.interval);
    }
  }

  /**
   * 自動再生停止
   *
   * @memberof ContentsModlue
   */
  @Action({})
  stop() {
    if (this.isVideo && this._video) {
      // 動画のときは、動画停止メソッドを呼び出す
      this._video.pause();

      // 待機時間が0以上のときは、待機時間後に自動再生復帰
      if (this.wait > 0) {
        this._timerId = setTimeout(() => {
          this.play();
        }, this.wait);
      }
    } else {
      this._timerId && clearTimeout(this._timerId);

      // 待機時間が0以上のときは、待機時間後に自動再生復帰
      if (this.wait > 0) {
        this._timerId = setTimeout(() => {
          this.next();
        }, this.wait);
      }
    }
    this.setPlaying(false);
  }
}

export default getModule(ContentsModlue, store);
